import { STORAGE_THEME } from "./i18n/copy";

export type Theme = "light" | "dark";

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function detectTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_THEME);
  if (stored === "light" || stored === "dark") return stored;
  return systemTheme();
}

function updateChrome(theme: Theme): void {
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#0a1410" : "#f4faf5");
  document
    .querySelector("#theme-toggle")
    ?.setAttribute("data-theme-active", theme);
  document.documentElement.dataset.theme = theme;
}

async function withViewTransition(apply: () => void): Promise<void> {
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { finished: Promise<void> };
  };
  if (
    typeof doc.startViewTransition === "function" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    try {
      await doc.startViewTransition(apply).finished;
      return;
    } catch {
      // fall through
    }
  }
  apply();
}

export async function applyTheme(theme: Theme): Promise<void> {
  await withViewTransition(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.classList.toggle("light", theme === "light");
    document.body.classList.toggle("dark", theme === "dark");
    document.body.classList.toggle("light", theme === "light");
    updateChrome(theme);
  });
  localStorage.setItem(STORAGE_THEME, theme);
  document.dispatchEvent(
    new CustomEvent("boorhub:theme", { detail: { theme } }),
  );
}

export async function toggleTheme(current: Theme): Promise<Theme> {
  const next: Theme = current === "dark" ? "light" : "dark";
  await applyTheme(next);
  return next;
}

export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    if (localStorage.getItem(STORAGE_THEME)) return;
    onChange(systemTheme());
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
